import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Chevron } from './Glyph';
import Dialog from './Dialog';
import { useAuth } from './AuthContext';
import { readPreference, writePreference } from '../lib/preferences';
import {
  arrange, columnKey, defaultLayout, frozenOffsets, moveColumn, parseLayout,
  serialiseLayout, setWidth, toggleHidden,
} from '../lib/tableLayout';
import { pressable } from '../lib/pressable';
import { usePageBarSlot } from './PageBar';

/*
  A list, drawn as a CSS grid.

  Written 5 September 2026 for "One shell, one menu, three homes", which owns
  rule 15 for the Group C screens.

  **Rule 15: lists are CSS grids, never HTML tables.** Learned the hard way on
  28 August 2026, when a drawing built from an HTML table rendered correctly in
  a browser and wrongly where it was published: `<col>` is a self-closing tag a
  parser can mishandle, and when it does, the whole table collapses into one
  enormous first column.

  **This is NOT "The shared table"**, which is its own job further down the
  queue and brings a header that stays put while you scroll, columns you can
  freeze, reorder, resize and hide, and a saved layout per person. This is the
  smaller thing rule 15 asks for: one `grid-template-columns` shared by the
  heading row and every row under it.

  **Below 1024 a row becomes a block.** A table with six columns on a 390 pixel
  screen is six columns of nothing, and the alternative that was there before
  was to leave the screen out entirely, which is what rule 21 forbids. Each cell
  keeps its heading beside it, so a person reading a stacked row still knows
  what each value is.

  **A list may hand in its own narrow shape instead**, through `stack`, added 5
  September 2026. Six labelled lines is the safe default and it is not always
  the right one: Group C's approved drawing of a region's locations at 390 puts
  the status and the rep together on one line under the name, three lines rather
  than six, because the labels say nothing a person could not already tell from
  the value. Both shapes are in the page and the stylesheet shows one, so
  nothing here has to know how wide the screen is.

  **The count of headings and the count of cells must match**, which is the other
  half of rule 15 and the half a person cannot see: a row one cell short pulls
  every column after it out of line, silently. It is checked here rather than
  left to a reviewer.
*/

/*
  **Every column needs a key of its own, and two blank headings do not have
  one.** Found 8 September 2026 on Account Mappings, which has a chip column and
  a Delete column, both unlabelled: `columnKey` falls back to the label, so both
  were keyed on the empty string, React treated them as one, and the Delete
  button was drawn in BOTH columns while the chip appeared in neither.

  The count check below was right the whole time, which is what makes this worth
  its own function: a list can have the right number of cells and still put them
  in the wrong places.
*/
export function keysAreDistinct(columns) {
  return new Set(columns.map(columnKey)).size === columns.length;
}

/*
  Rule 15's other half, and the half a person cannot see: **the count of headings
  and the count of cells must match.** A row one cell short pulls every column
  after it out of line, silently, from that row down.

  Pulled out as its own function so it can be tested, because this project has
  no way to render a component in a test.
*/
export function columnsMatch(headingCount, cellCount) {
  return headingCount === cellCount;
}

/*
  **A list may have a middle shape as well**, added 5 September 2026.

  Group C's approved drawings of a region's locations show three shapes, not
  two: six columns on a desk, four on a tablet and a large phone, and a block on
  a phone. The middle one drops the two columns a narrower screen can spare, the
  rep and the date it was handed in, and puts the rep under the location's name
  instead. A list asks for it by marking those columns `hideMid`, and then its
  block form starts at 640 rather than 1024.

  A list that marks nothing is untouched: it keeps the two shapes it had, and
  the screens signed off in Group D draw exactly as they did.
*/
/*
  The middle shape's column widths, one for every column that is still shown.

  Its own function so it can be tested, for the same reason `columnsMatch`
  is: a template with one track too many or too few is a list one column out
  of line, and nothing raises anything. The stylesheet hides the dropped
  columns, so the count here has to agree with the count of columns not marked
  `hideMid`, exactly.
*/
export function midTemplateOf(columns) {
  return columns.filter(c => !c.hideMid)
    .map(c => c.midWidth || c.width || '1fr')
    .join(' ');
}

/*
  Three things a list of count lines needs that no Group C list did, added 5
  September 2026 when count entry's three layouts became one:

  - **`rowClass`**, so a row can carry its own colour. A line flagged as dead
    stock, one flagged as non-inventory and one that is not in the catalog are
    each tinted, and rule 11's state bar down a row's left edge says whether the
    line has been counted.
  - **`sort`**, so a heading can be pressed to order the list. Count entry had
    that in an HTML table and rule 15 asks for a grid, so the grid has to be
    able to do it or the change would take something away.
  - **`stickyHead`**, so the headings stay put while 486 lines scroll under
    them.

  All three are optional and every list written before this draws exactly as it
  did.
*/
/*
  **`layoutKey` is what turns this list into "the shared table"**, added 6
  September 2026. Give one and the columns can be frozen, reordered, resized and
  hidden, and the arrangement is kept against the person rather than the browser,
  so it follows them from a laptop to a tablet. Give none and this draws exactly
  as it did: every list written before this is untouched.

  **All of it is a desk thing.** Below 1024 a row is a block with its headings
  beside each value, so there are no columns to arrange. The chooser is not shown
  and the saved arrangement simply waits.
*/
export default function ListGrid({ columns, rows, groups, keyOf, cells, stack, empty, onRowClick, rowClass, sort, onSort, stickyHead, layoutKey, layoutTitle }) {
  const { layout, setLayout, ready } = useTableLayout(layoutKey, columns);
  const [chooser, setChooser] = useState(false);

  /*
    **The Columns button goes up into the page bar**, the owner's decision of 3
    October 2026: *"what is this and why is it in this location? looks weird by
    itself."* On its own strip above the table it floated on the background, and
    once the page went as wide as the bar earlier the same day it sat stranded in
    the far corner.

    **The strip stays as the fallback** for the two cases where there is no bar
    to go into: a list drawn inside a page that has no page bar of its own, and
    any width under 1024, where the bar is not on screen and the stylesheet hides
    the strip anyway because a stacked row has no columns to arrange.
  */
  /* **An empty list has nothing to arrange**, and the strip was never drawn for
     one, because the early return below comes first. The button in the bar has
     to be held back in the same case or an empty screen grows a control the old
     one did not have. */
  const anyRows = (groups ? groups.reduce((n, g) => n + g.rows.length, 0) : (rows || []).length) > 0;
  const { barInBar, setTableControl } = usePageBarSlot();
  const upInBar = barInBar && !!layoutKey && anyRows;
  const open = useCallback(() => setChooser(true), []);
  useEffect(() => {
    if (!upInBar) return undefined;
    setTableControl(layoutKey, { label: 'Columns', open });
    return () => setTableControl(layoutKey, null);
  }, [upInBar, layoutKey, open, setTableControl]);

  // The columns as this person has them, and where each one's values live in
  // the array `cells` hands back, which is always in the ORIGINAL order.
  const shown = layoutKey && ready ? arrange(columns, layout) : columns;
  const source = new Map(columns.map((c, i) => [columnKey(c), i]));
  const pick = shown.map(c => source.get(columnKey(c)));

  const template = shown.map(c => c.width || '1fr').join(' ');
  const hasMid = shown.some(c => c.hideMid);
  const midTemplate = midTemplateOf(shown);
  const offsets = layoutKey ? frozenOffsets(shown, layout.frozen) : [];

  const drag = useRef(null);
  function startResize(e, key, el) {
    e.preventDefault();
    e.stopPropagation();
    drag.current = { key, x: e.clientX, from: el.getBoundingClientRect().width };
    const move = ev => {
      const d = drag.current;
      if (!d) return;
      setLayout(l => ({ ...l, widths: setWidth(l.widths, d.key, d.from + (ev.clientX - d.x)) }));
    };
    const up = () => {
      drag.current = null;
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  }

  /*
    **A list can arrive in groups**, added 6 September 2026 so Count History can
    band its rows under each location and still be ONE table. Drawn as separate
    tables it would have been one Columns button per band, each with its own
    copy of the arrangement, and changing one would not have changed the rest.
    A group hands in its own heading; this only puts it between the runs of rows.
  */
  const runs = groups || [{ key: '', header: null, rows: rows || [] }];
  const total = runs.reduce((n, g) => n + g.rows.length, 0);

  if (total === 0) {
    return (
      <div style={{ padding: '28px 20px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>
        {empty || 'Nothing here yet.'}
      </div>
    );
  }

  const grid = (
    <div className={'lgrid' + (hasMid ? ' lgrid-mid' : '')}
      style={{ '--lgrid-cols': template, '--lgrid-cols-mid': midTemplate }}>
      <div className={'lgrid-head' + (stickyHead ? ' is-sticky' : '')} role="row">
        {shown.map((c, i) => {
          const sortable = c.sortKey && onSort;
          const on = sortable && sort?.col === c.sortKey;
          const frozen = offsets[i] !== undefined;
          return (
            <div key={columnKey(c)}
              className={'lgrid-h' + (c.hideMid ? ' lgrid-hide-mid' : '') + (sortable ? ' is-sortable' : '') + (frozen ? ' is-frozen' : '')}
              style={{ textAlign: c.align || 'left', ...(frozen ? { left: offsets[i] } : null) }}
              {...pressable(sortable ? () => onSort(c.sortKey) : undefined)}>
              {c.label}{on ? (sort.dir === 'asc' ? ' [A-Z]' : ' [Z-A]') : ''}
              {layoutKey && (
                <span className="lgrid-grip"
                  onMouseDown={e => startResize(e, columnKey(c), e.currentTarget.parentNode)} />
              )}
            </div>
          );
        })}
      </div>
      {runs.map(g => (
        <React.Fragment key={g.key}>
          {g.header}
          {g.rows.map((r, i) => {
            const values = cells(r, i);
            /*
              **Two columns with no heading collide, and the collision is
              silent.** Found 8 September 2026 on Account Mappings, which has a
              chip column and a Delete column, both unlabelled. `columnKey`
              falls back to the label, so both were keyed on the empty string,
              React treated them as one, and the Delete button was drawn in
              BOTH: two Delete buttons on every row and the chip nowhere.

              A list that renders the wrong cell in the wrong column is exactly
              the fault the count check above exists to catch, and the count was
              right the whole time. So this is checked too, and loudly.
            */
            if (!keysAreDistinct(columns)) {
              throw new Error(
                'ListGrid: two columns share a key. Give every column a distinct '
                + 'label, or a `key` of its own where the heading is blank.',
              );
            }
            if (!columnsMatch(columns.length, values.length)) {
              // Loud on purpose. A quiet mismatch is a list that looks fine and
              // is one column out from the third row down.
              throw new Error(
                `ListGrid: ${columns.length} headings and ${values.length} cells. They have to match.`,
              );
            }
            return (
              <div
                key={keyOf ? keyOf(r, i) : i}
                className={'lgrid-row' + (onRowClick ? ' is-tappable' : '') + (stack ? ' has-stack' : '') + (rowClass ? ' ' + rowClass(r) : '')}
                {...pressable(onRowClick ? () => onRowClick(r) : undefined)}
                role="row"
              >
                {pick.map((ci, at) => (
                  <div key={columnKey(shown[at])}
                    className={'lgrid-c' + (shown[at].hideMid ? ' lgrid-hide-mid' : '') + (offsets[at] !== undefined ? ' is-frozen' : '')}
                    data-label={shown[at].label}
                    style={{ textAlign: shown[at].align || 'left', ...(offsets[at] !== undefined ? { left: offsets[at] } : null) }}>
                    {values[ci]}
                  </div>
                ))}
                {stack && <div className="lgrid-stack">{stack(r, i)}</div>}
              </div>
            );
          })}
        </React.Fragment>
      ))}
    </div>
  );

  if (!layoutKey) return grid;

  return (
    <>
      {!upInBar && (
        <div className="lgrid-bar">
          <button className="lgrid-cols" onClick={open}>Columns</button>
        </div>
      )}
      <div className="lgrid-scroll">{grid}</div>
      {chooser && (
        <ColumnChooser
          title={layoutTitle || 'Columns'}
          columns={columns} layout={layout} setLayout={setLayout}
          onClose={() => setChooser(false)}
        />
      )}
    </>
  );
}

/*
  One person's arrangement of one table, read once and written whenever they
  change it.

  **A write that does not land puts the screen back.** `writePreference` reads
  the row back and answers false when nothing was written, which is this
  project's own hardest-learned rule: a save that no rule allows changes no rows,
  raises nothing, and answers "fine". Without this the columns would move on
  screen and be exactly as they were on the next visit.
*/
function useTableLayout(layoutKey, columns) {
  const { profile } = useAuth();
  const [layout, setLayoutState] = useState(() => defaultLayout(columns));
  const [ready, setReady] = useState(!layoutKey);
  const saved = useRef(null);

  useEffect(() => {
    if (!layoutKey) { setReady(true); return undefined; }
    let live = true;
    setReady(false);
    readPreference(profile?.id, TABLE_PREFIX + layoutKey, null).then(text => {
      if (!live) return;
      const next = parseLayout(text, columns);
      saved.current = serialiseLayout(next);
      setLayoutState(next);
      setReady(true);
    });
    return () => { live = false; };
    // eslint-disable-next-line
  }, [layoutKey, profile?.id]);

  const setLayout = useCallback(next => {
    setLayoutState(prev => {
      const value = typeof next === 'function' ? next(prev) : next;
      const text = serialiseLayout(value);
      if (text !== saved.current && profile?.id) {
        const was = saved.current;
        saved.current = text;
        writePreference(profile.id, TABLE_PREFIX + layoutKey, text).then(ok => {
          if (!ok) { saved.current = was; setLayoutState(parseLayout(was, columns)); }
        });
      }
      return value;
    });
    // eslint-disable-next-line
  }, [layoutKey, profile?.id]);

  return { layout, setLayout, ready };
}

export const TABLE_PREFIX = 'table.';

/*
  Choosing what the columns do. A dialog on rule 34's shell rather than a menu
  hanging off a heading, for the same reason the Reports filters are: a menu
  pinned to a heading has nowhere to go below 1024 and no room to say what it is
  doing.
*/
function ColumnChooser({ title, columns, layout, setLayout, onClose }) {
  const byKey = new Map(columns.map(c => [columnKey(c), c]));
  const visible = layout.order.filter(k => !layout.hidden.includes(k));

  return (
    <Dialog
      kind="info" icon={<IconColumns />} title={title}
      says="Drag a heading's edge to make a column wider. What you set here is kept against you, so it follows you to another machine."
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-quiet" onClick={() => setLayout(defaultLayout(columns))}>
            Put them back
          </button>
          <button className="btn btn-primary" onClick={onClose}>Done</button>
        </>
      }
    >
      <div className="rp-ticks">
        {layout.order.map((k, i) => {
          const col = byKey.get(k);
          if (!col) return null;
          const on = !layout.hidden.includes(k);
          const place = visible.indexOf(k);
          return (
            <div key={k} className={'rp-tick' + (on ? ' on' : '')}>
              <input type="checkbox" checked={on}
                onChange={() => setLayout(l => ({ ...l, hidden: toggleHidden(l.hidden, k, l.order) }))} />
              <span className="rp-tick-l">{col.label}</span>
              {on && place >= 0 && place < layout.frozen && <span className="rp-tick-s">Frozen</span>}
              <span className="lgrid-move">
                <button className="lgrid-mv" aria-label="Move up" disabled={i === 0}
                  onClick={() => setLayout(l => ({ ...l, order: moveColumn(l.order, k, i - 1) }))}><Chevron dir="up" /></button>
                <button className="lgrid-mv" aria-label="Move down" disabled={i === layout.order.length - 1}
                  onClick={() => setLayout(l => ({ ...l, order: moveColumn(l.order, k, i + 1) }))}><Chevron dir="down" /></button>
              </span>
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 16 }}>
        <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1.3, textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: 6 }}>
          Columns that stay put when you scroll sideways
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button className="btn btn-quiet" disabled={layout.frozen === 0}
            onClick={() => setLayout(l => ({ ...l, frozen: Math.max(0, l.frozen - 1) }))}>Fewer</button>
          <span style={{ fontSize: 14, fontWeight: 800, minWidth: 90, textAlign: 'center' }}>
            {layout.frozen === 0 ? 'None' : layout.frozen + (layout.frozen === 1 ? ' column' : ' columns')}
          </span>
          <button className="btn btn-quiet" disabled={layout.frozen >= visible.length - 1}
            onClick={() => setLayout(l => ({ ...l, frozen: l.frozen + 1 }))}>More</button>
        </div>
        {/* A column measured in fr has no width until the row is laid out, so
            freezing stops there rather than sticking it in the wrong place. */}
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 8, lineHeight: 1.55 }}>
          A column can only stay put if it has a fixed width. Drag its edge to give it one.
        </div>
      </div>
    </Dialog>
  );
}

const IconColumns = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="6" height="16" rx="1.5" />
    <rect x="11" y="4" width="4" height="16" rx="1.5" />
    <rect x="17" y="4" width="4" height="16" rx="1.5" />
  </svg>
);
